import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Security as SecurityIcon,
  SwapHoriz as MigrationIcon,
  VerifiedUser as VerifyIcon,
} from "@mui/icons-material";

interface RegistrationPinModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (pin: string) => void;
}

const RegistrationPinModal: React.FC<RegistrationPinModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(value);
    if (value.length === 6) setError(false);
  };

  const handleConfirm = () => {
    if (pin.length !== 6) {
      setError(true);
      return;
    }
    onConfirm(pin);
    onClose();
    setPin(""); // Reset for next time
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          padding: "12px",
          width: "100%",
          maxWidth: "450px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          fontSize: "24px",
          color: "#1A1A2E",
          textAlign: "center",
          pb: 1,
        }}
      >
        Set Registration PIN
      </DialogTitle>
      <DialogContent>
        <Typography
          sx={{
            mb: 3,
            color: "text.secondary",
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          To complete your WhatsApp Business setup, please choose a 6-digit PIN.
          This PIN is essential for:
        </Typography>

        <List sx={{ mb: 3, bgcolor: "rgba(0,0,0,0.02)", borderRadius: "12px" }}>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: "#4D1277" }}>
              <SecurityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Two-Step Verification"
              secondary="Adds an extra layer of security to your account."
              primaryTypographyProps={{ fontWeight: 600, fontSize: "13px" }}
              secondaryTypographyProps={{ fontSize: "11px" }}
            />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: "#4D1277" }}>
              <MigrationIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Account Migration"
              secondary="Needed if you move this number to a different platform."
              primaryTypographyProps={{ fontWeight: 600, fontSize: "13px" }}
              secondaryTypographyProps={{ fontSize: "11px" }}
            />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: "#4D1277" }}>
              <VerifyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Business Identity"
              secondary="Meta may ask for this PIN to verify your business status."
              primaryTypographyProps={{ fontWeight: 600, fontSize: "13px" }}
              secondaryTypographyProps={{ fontSize: "11px" }}
            />
          </ListItem>
        </List>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            fullWidth
            label="6-Digit PIN"
            variant="outlined"
            value={pin}
            onChange={handlePinChange}
            error={error}
            helperText={error ? "Please enter exactly 6 digits" : ""}
            inputProps={{
              maxLength: 6,
              style: {
                textAlign: "center",
                fontSize: "24px",
                letterSpacing: "12px",
                fontWeight: 700,
              },
            }}
            placeholder="000000"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1, justifyContent: "center", gap: 2 }}>
        <Button
          onClick={onClose}
          sx={{ color: "text.secondary", fontWeight: 700 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={pin.length !== 6}
          sx={{
            backgroundColor: "#4D1277",
            borderRadius: "10px",
            px: 4,
            py: 1,
            fontWeight: 700,
            "&:hover": { backgroundColor: "#3a0e5a" },
            "&.Mui-disabled": { backgroundColor: "rgba(0,0,0,0.12)" },
          }}
        >
          Complete Registration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationPinModal;
